import { ReactNode, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { cn } from '@restworld/utils-common';
import { useFloating, offset, flip, shift, size, autoUpdate, Placement } from '@floating-ui/react';

import { IconComponentType } from '../icon-button';
import {
  ArrowIconWrapper,
  StyledSelectHelperText,
  HelperTextVariantType,
  ListboxButtonContent,
  ListboxContentWrapper,
  SelectMainWrapper,
  StyledPlaceholder,
  SecondaryLabel,
  selectLabelStyle,
  selectInteractiveAreaStyle,
  selectOptionsStyle,
  selectOptionStyle,
} from './select.helpers';

export function Select({
  disabled,
  label,
  secondaryLabel,
  value,
  onChange,
  options,
  fullwidth,
  isContentCentered,
  className,
  helperText: { variant: helperTextVariant, message: helperTextMessage } = { variant: 'error', message: '' },
  placeholder = '',
  tertiaryLabel = '',
  optionListPlacement = 'bottom',
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const placeholderEl = useMemo(() => <StyledPlaceholder>{placeholder}</StyledPlaceholder>, [placeholder]);

  const { refs, floatingStyles } = useFloating({
    strategy: 'fixed',
    placement: optionListPlacement,
    middleware: [
      offset(4),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const handleToggle = useCallback(() => {
    if (!disabled) setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleSelect = useCallback(
    (option: SelectOptionType) => {
      onChange?.(option);
      setIsOpen(false);
    },
    [onChange]
  );

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check both the wrapper and the floating portal element
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        (!refs.floating.current || !refs.floating.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, refs.floating]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!options?.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1));
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex]);
        } else {
          setIsOpen(true);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [isOpen, highlightedIndex, options, handleSelect]
  );

  return (
    <SelectMainWrapper fullwidth={fullwidth} className={className}>
      <div ref={wrapperRef} role="listbox" onKeyDown={handleKeyDown}>
        <ListboxContentWrapper>
          {label && (
            <label className={cn(selectLabelStyle, disabled && 'text-neutral-100')} aria-disabled={disabled}>
              <span>{label}</span> <SecondaryLabel>{secondaryLabel}</SecondaryLabel>
            </label>
          )}
          <button
            type="button"
            className={cn(
              selectInteractiveAreaStyle,
              disabled && 'bg-neutral-40 hover:bg-neutral-40 cursor-not-allowed'
            )}
            aria-disabled={disabled}
            aria-expanded={isOpen}
            ref={refs.setReference}
            onClick={handleToggle}
          >
            <ListboxButtonContent className={cn('flex items-center gap-1', isContentCentered && 'mx-auto')}>
              {tertiaryLabel ? <span>{tertiaryLabel}</span> : null}
              {value?.Icon ? <value.Icon className="text-neutral-700 h-4 w-4 min-w-4 min-h-4" /> : null}
              {value?.icon ? value.icon : null}
              {value ? value.label : placeholderEl}
            </ListboxButtonContent>
            <ArrowIconWrapper isOpen={isOpen}>
              <ChevronDownIcon className="h-6 w-6 text-neutral-700" aria-hidden="true" />
            </ArrowIconWrapper>
          </button>
          {isOpen &&
            typeof document !== 'undefined' &&
            ReactDOM.createPortal(
              <ul
                className={selectOptionsStyle}
                style={{ ...floatingStyles, pointerEvents: 'auto' }}
                ref={refs.setFloating}
                role="listbox"
              >
                {options?.map((option, index) => {
                  const { Icon, label: optionLabel, icon } = option;

                  return (
                    <li
                      key={index}
                      role="option"
                      aria-selected={value?.value === option.value}
                      // Use onMouseDown + preventDefault to avoid focus leaving
                      // the trigger before selection (prevents Radix Dialog dismissal)
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(option);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        selectOptionStyle,
                        'flex items-center gap-2',
                        isContentCentered && 'justify-center'
                      )}
                    >
                      {Icon ? <Icon className="text-neutral-700 h-6 w-6" /> : null}
                      {icon ? icon : null}
                      <span>{optionLabel}</span>
                    </li>
                  );
                })}
              </ul>,
              document.body
            )}
        </ListboxContentWrapper>
        {helperTextMessage && (
          <StyledSelectHelperText variant={helperTextVariant}>{helperTextMessage}</StyledSelectHelperText>
        )}
      </div>
    </SelectMainWrapper>
  );
}
export default Select;
export interface SelectProps {
  placeholder?: string;
  disabled?: boolean;
  helperText?: { variant?: HelperTextVariantType; message: string };
  label?: string;
  secondaryLabel?: string;
  value?: SelectOptionType;
  onChange?: (value: SelectOptionType) => void;
  options?: SelectOptionType[];
  fullwidth?: boolean;
  tertiaryLabel?: string;
  optionListPlacement?: Placement;
  icon?: ReactNode;
  isContentCentered?: boolean;
  className?: string;
}
export type SelectOptionType = {
  value: string;
  Icon?: IconComponentType;
  label?: string;
  key?: string;
  icon?: ReactNode;
};
